'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import { Bell, Search, User } from 'lucide-react';

interface TopNavProps {
  activeRoute: string;
  lang: 'en' | 'ta';
  onLangToggle?: () => void;
}

export default function TopNav({ activeRoute, lang, onLangToggle }: TopNavProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-card/90 backdrop-blur-xl border-b border-border shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2.5 group">
          <AppLogo size={64} />
          <div className="flex flex-col leading-tight">
            <span className="font-bold text-base text-foreground tracking-tight">
              Koyambedu Today
            </span>
            <span className="text-xs text-muted-foreground hidden sm:block">
              {lang === 'en' ? 'Fresh Market Intelligence' : 'புதிய சந்தை தகவல்'}
            </span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {[
            { href: '/', label: lang === 'en' ? 'Today' : 'இன்று' },
            { href: '/product-category-browse', label: lang === 'en' ? 'Browse' : 'உலாவு' },
            { href: '/watchlist-alert-settings', label: lang === 'en' ? 'Watchlist' : 'கண்காணிப்பு' },
          ].map(item => (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                activeRoute === item.href
                  ? 'bg-secondary text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onLangToggle}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary border border-border text-sm font-semibold text-primary transition-all duration-200 hover:bg-green-light active:scale-95"
            aria-label="Toggle language"
          >
            <span className={lang === 'en' ? 'text-primary' : 'text-muted-foreground'}>EN</span>
            <span className="text-border">|</span>
            <span className={lang === 'ta' ? 'text-primary' : 'text-muted-foreground'}>தமிழ்</span>
          </button>

          <Link
            href="/product-category-browse"
            className="w-9 h-9 flex items-center justify-center rounded-xl text-muted-foreground hover:text-primary hover:bg-secondary transition-all duration-200"
            aria-label="Search products"
          >
            <Search size={18} />
          </Link>

          <Link
            href="/watchlist-alert-settings"
            className="relative w-9 h-9 flex items-center justify-center rounded-xl text-muted-foreground hover:text-primary hover:bg-secondary transition-all duration-200"
            aria-label="Notifications"
          >
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent rounded-full" />
          </Link>

          <Link
            href="/sign-up-login-screen"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary text-primary-foreground transition-all duration-200 hover:opacity-90 active:scale-95"
            aria-label="Account"
          >
            <User size={16} />
          </Link>
        </div>
      </div>
    </header>
  );
}